// Generated by tutorials/record.cjs from tutorials/articles/h41-reading-a-menu-cycle.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h41 --text-only` (wording) or
// `node tutorials/record.cjs h41` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'reading-a-menu-cycle',
  catalogId: 'h41',
  title: 'Reading a menu cycle',
  summary: 'Open a menu cycle and read any day and meal: the menu categories and items, what each diet type and texture gets, and what each diet\'s tray ends up with.',
  audience: 'Menu planners, dietitians and dietary managers who check the menu',
  area: 'menus',
  learn: [
    'Open a menu cycle to look at it without changing anything',
    'Go to a day and a meal',
    'Read the menu categories, choices and the items that go with them',
    'Read the diet and texture columns: served, not served, a substitute, a texture change',
    'See what each diet\'s tray gets for a row',
    'See the whole cycle at a glance',
  ],
  keywords: [
    'menu cycle',
    'modify',
    'menu',
    'read menu',
    'day',
    'meal',
    'mealtime',
    'menu category',
    'choice',
    'diet type',
    'consistency',
    'texture',
    'served',
    'not served',
    'override',
    'substitute',
    'modification',
    'not allowed',
    'legend',
    'preview',
    'menu preview',
    'row info',
    'overview',
    'extended view',
  ],
  steps: [
    {
      id: 'open',
      part: 'Open a cycle',
      heading: 'Open the cycle with Modify',
      text: 'On the Menu Cycles page, select the cycle, then Modify. The cycle opens on its first day.\n\nIt opens read only: you can look at every day and meal without changing anything.',
      media: {
        image: {
          src: '/help/reading-a-menu-cycle/open.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/reading-a-menu-cycle/open.mp4',
          width: 1280,
          height: 842,
          durationSec: 5.9,
        },
        alt: 'Fall/Winter Cycle 2026 open on Day 1 Morning, read only, with the juice, hot cereal and entree rows and a check mark or a substitute in each diet column.',
      },
    },
    {
      id: 'day-meal',
      part: 'Open a cycle',
      heading: 'Go to a day and a meal',
      text: 'Select the day number and type the day you want, then press Enter. Under it is the date that day falls on next.\n\nThen pick the meal: Morning, Afternoon or Evening.',
      media: {
        image: {
          src: '/help/reading-a-menu-cycle/day-meal.webp',
          width: 1280,
          height: 516,
        },
        clip: {
          src: '/help/reading-a-menu-cycle/day-meal.mp4',
          width: 1280,
          height: 516,
          durationSec: 6,
        },
        alt: 'Day 22 Afternoon, with the soup, the two entree choices, the starch, the vegetable and the dessert of that lunch.',
      },
    },
    {
      id: 'rows',
      part: 'Read the rows',
      heading: 'Read the menu categories and items',
      text: 'Each grey row is a menu category, such as SOUP, ENTREE or DESSERT. The items in it are listed under it.\n\n1st and 2nd are the choices in that category: here, the main entree and the alternate. An indented row with an arrow goes with the item above it, such as a dressing with a salad or margarine with a roll.\n\nBeside each item is its portion size. AIM and a number is how many Auto Item Mods the item has.',
      media: {
        image: {
          src: '/help/reading-a-menu-cycle/rows.webp',
          width: 672,
          height: 536,
        },
        alt: 'The ENTREE category with SPAGHETTI MEAT SAUCE as the 1st choice and CHEF SALAD as the 2nd, with DRESSING RANCH PC indented under the salad.',
      },
    },
    {
      id: 'diets',
      part: 'Read the rows',
      heading: 'Read the diet columns',
      text: 'Each column is a diet on this menu. A check mark means the diet is served the row\'s item. An X means it is not served at all.\n\nA diamond with an item name means that diet gets a substitute instead: here Renal trays get NOODLES BUTTERED in place of the spaghetti, and Cardiac and Low Sodium 2g trays get a low sodium soup.',
      media: {
        image: {
          src: '/help/reading-a-menu-cycle/diets.webp',
          width: 1280,
          height: 842,
        },
        alt: 'The diet type columns Regular to Liberal Geriatric, with check marks, a red X for Renal on the dessert, and substitutes such as SOUP LOW SODIUM and NOODLES BUTTERED.',
      },
    },
    {
      id: 'legend',
      part: 'Read the rows',
      heading: 'Check the legend',
      text: 'The line above the grid says what each mark means, for diet types and for consistencies. Keep it in view until the marks are familiar.',
      media: {
        image: {
          src: '/help/reading-a-menu-cycle/legend.webp',
          width: 942,
          height: 66,
        },
        alt: 'The legend: for diet types a check mark for served, an X for not served and a diamond for an override; for consistencies a wrench for a modification, an X for not allowed, and blank for no rule.',
      },
    },
    {
      id: 'textures',
      part: 'Read the rows',
      heading: 'Read the texture and liquid columns',
      text: 'Scroll the grid to the right for the consistencies: the textures (Mechanical Soft, Minced and Moist, Pureed) and the thickened liquids.\n\nA wrench means that texture gets a modification, such as the item made Pureed, or a different item. An X means the item is not allowed on that texture. Blank means no rule.',
      media: {
        image: {
          src: '/help/reading-a-menu-cycle/textures.webp',
          width: 1280,
          height: 842,
        },
        alt: 'The consistency columns Mechanical Soft to Moderately Thick: wrenches with Minced and Moist and Pureed on the soup and the spaghetti, and X marks for the chef salad on the soft textures.',
      },
    },
    {
      id: 'preview',
      part: 'See what each tray gets',
      heading: 'See what each diet\'s tray gets',
      text: 'Select an item row. Menu Preview, on the right, lists what each diet order gets for that row, with the diets that get the same thing grouped together. Override marks a substitute or a texture change.\n\nA diet that does not get the row at all is not listed.',
      media: {
        image: {
          src: '/help/reading-a-menu-cycle/preview.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/reading-a-menu-cycle/preview.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.8,
        },
        alt: 'SPAGHETTI MEAT SAUCE selected, and the Menu Preview listing the regular serving for most diets, NOODLES BUTTERED for Renal, and the Mechanical Soft, Minced and Moist and Pureed servings.',
      },
    },
    {
      id: 'row-info',
      part: 'See what each tray gets',
      heading: 'Read the row in words',
      text: 'Select Row info for what is stored on the selected row: its size, its choice, and each diet\'s setting in words.',
      media: {
        image: {
          src: '/help/reading-a-menu-cycle/row-info.webp',
          width: 520,
          height: 988,
        },
        clip: {
          src: '/help/reading-a-menu-cycle/row-info.mp4',
          width: 520,
          height: 988,
          durationSec: 3.7,
        },
        alt: 'Row info for SPAGHETTI MEAT SAUCE: size 1 cup, 1st choice, and under Diet types Serves as-is for most diets and NOODLES BUTTERED for Renal.',
      },
    },
    {
      id: 'extended',
      part: 'See what each tray gets',
      heading: 'Show the full text in every cell',
      text: 'Select Extended to widen the columns and spell out every override in full. Select it again to go back.',
      media: {
        image: {
          src: '/help/reading-a-menu-cycle/extended.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/reading-a-menu-cycle/extended.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.1,
        },
        alt: 'Extended view: wider diet columns, with Item : SOUP LOW SODIUM on the soup for Cardiac and Item : NOODLES BUTTERED on the spaghetti for Renal.',
      },
    },
    {
      id: 'view-options',
      part: 'See what each tray gets',
      heading: 'Choose what the page shows',
      text: 'Select View options (the sliders at the top right) to show or hide parts of the page: the AIM count, the Menu Preview, the plate color analysis, the AMC and Direct columns, or only the cells that are exceptions.',
      media: {
        image: {
          src: '/help/reading-a-menu-cycle/view-options.webp',
          width: 1050,
          height: 942,
        },
        clip: {
          src: '/help/reading-a-menu-cycle/view-options.mp4',
          width: 1050,
          height: 942,
          durationSec: 3.7,
        },
        alt: 'The View options panel with AIM Count, Extended View, Menu Preview, Plate Color Analysis, Only show exceptions, Compact columns and AMC and Direct columns.',
      },
    },
    {
      id: 'overview',
      part: 'See what each tray gets',
      heading: 'See the whole cycle at a glance',
      text: 'Select Overview for every day and meal of the cycle, week by week, with the number of items in each. Select a meal to go to it.\n\nEdited recently and Needs attention narrow it to meals changed lately, or meals with something to check.',
      media: {
        image: {
          src: '/help/reading-a-menu-cycle/overview.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/reading-a-menu-cycle/overview.mp4',
          width: 1280,
          height: 842,
          durationSec: 3.8,
        },
        alt: 'The Menu overview of Fall/Winter Cycle 2026: four weeks of days, each with Morning, Afternoon and Evening and the number of items in each meal.',
      },
    },
  ],
  tips: [
    'Select Today to go to the cycle day today falls on, or the calendar button to pick any date.',
    'The arrows beside the day go back and forward one day. Past the last day, the cycle starts again at day 1.',
    'Keyboard: T goes to today, E switches Extended view on and off, and P shows or hides the Menu Preview.',
    'Hover over a cell to see, in words, what that diet gets.',
  ],
  notes: [
    'The menu opens read only every time, so looking cannot change anything. To make changes, select Edit menu (see Changing items and rows).',
    'Morning, Afternoon and Evening are the facility\'s three meals: breakfast, lunch and dinner.',
    'A blank texture cell means no rule: that texture gets the row\'s item, made the way the kitchen makes that texture.',
  ],
  related: [
    'menu-cycles',
    'changing-menu-items',
    'diet-and-consistency-rules',
    'holiday-and-one-time-menus',
    'synced-rows-and-push-to',
    'menu-analysis',
  ],
  recorded: '2026-10-26',
};

export default article;
